import React, { useState, useEffect, useMemo } from 'react'
import { useTranslate } from 'react-admin'
import LoadingButton from '@mui/lab/LoadingButton'
import SaveIcon from '@mui/icons-material/Save'
import { Dialog, DialogContent, DialogTitle, DialogActions, Button, TextField, List, ListItem, ListItemText, CircularProgress, Typography, Box } from '@mui/material'
import { useCardId } from 'src/components/pages/sales/reservation/context/CardIdContext'
import { basesServices } from 'src/services/reservation/bases/Bases.service'

export const ReAssignModalBase = ({
  open,
  handleClose,
  titleModal,
  onSubmitCustom,
}) => {
  const translate = useTranslate()
  const [basesList, setBasesList] = useState([])
  const [selectedBase, setSelectedBase] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const { cardId } = useCardId()

  useEffect(() => {
    if (open) {
      fetchBases()
    }
    return () => {
      setSelectedBase(null)
      setSearchTerm('')
    }
  }, [open])

  const fetchBases = async () => {
    setLoading(true)
    try {
      const data = await basesServices.getByAll()
      setBasesList(data)
    } catch (error) {
      setBasesList([])
    } finally {
      setLoading(false)
    }
  }

  const filteredBases = useMemo(() => {
    if (!searchTerm) return basesList
    return basesList.filter((base) => base.name.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [basesList, searchTerm])

  const handleSave = async () => {
    if (!selectedBase) return
    setSaving(true)
    try {
      await onSubmitCustom(selectedBase.id, cardId)
    } finally {
      setSaving(false)
    }
  }

  const handleCloseModal = () => {
    setSelectedBase(null)
    setSearchTerm('')
    handleClose()
  }

  return (
    <Dialog open={open} maxWidth='sm' fullWidth>
      <DialogTitle>{titleModal}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            size='small'
            placeholder={translate('ra.action.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
          />
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
              {filteredBases.length === 0 ? (
                <ListItem>
                  <ListItemText primary={translate('ra.message.no_results')} />
                </ListItem>
              ) : (
                filteredBases.map((base) => (
                  <ListItem
                    key={base.id}
                    button
                    selected={selectedBase?.id === base.id}
                    onClick={() => setSelectedBase(base)}
                    sx={{
                      '&.Mui-selected': { backgroundColor: '#e3f2fd' },
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                  >
                    <ListItemText primary={base.name} />
                  </ListItem>
                ))
              )}
            </List>
          )}
          {selectedBase && (
            <Typography variant='body2' sx={{ mt: 2, color: 'text.secondary' }}>
              {translate('ra.field.selected')}: <strong>{selectedBase.name}</strong>
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <LoadingButton
          loading={saving}
          variant='contained'
          onClick={handleSave}
          startIcon={<SaveIcon />}
          disabled={!selectedBase}
        >
          {translate('ra.action.save')}
        </LoadingButton>
        <Button onClick={handleCloseModal} variant='outlined' color='error'>
          {translate('ra.action.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
